'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLocale } from 'next-intl';

interface Service {
  id: number;
  basliq: string;
  aciqlama: string;
  ozellik: string;
  ozellik_1: string;
  ozellik_2: string;
  ozellik_3: string;
  foto: {
    url: string;
  };
}

export default function ServicesPage() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const locale = useLocale();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(
          // `https://api.omtransport.az/api/services?populate=*&locale=${locale}`
          `https://api.omtransport.az/api/services?populate=*`
        );
        const data = await response.json();
        setServices(data.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [locale]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <section
      className="py-12 sm:py-16 md:py-20 relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-blue-50"
      ref={ref}
    >
      {/* Dekoratif Elementler */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-white to-transparent" />
        <motion.div
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 50,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70"
        />
        <motion.div
          animate={{
            rotate: [360, 0],
          }}
          transition={{
            duration: 50,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-70"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-base text-blue-600 font-semibold tracking-wide uppercase"
          >
            Xidmətlərimiz
          </motion.h2>
          <motion.h3
            variants={itemVariants}
            className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl"
          >
            Sizə təklif etdiyimiz xidmətlər
          </motion.h3>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-500"
            >
              {/* Servis Resmi */}
              <div className="relative h-64 w-full overflow-hidden">
                <Image
                  src={`https://api.omtransport.az${service.foto.url}`}
                  alt={service.basliq}
                  fill
                  className="object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent" />
              </div>

              {/* İçerik */}
              <div className="p-8 bg-gradient-to-b from-white/0 to-white/100">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                  {service.basliq}
                </h4>
                <p className="text-gray-600 mb-6 group-hover:text-gray-900 transition-colors duration-300">
                  {service.aciqlama}
                </p>

                {/* Özellikler Listesi */}
                <ul className="space-y-3">
                  {[
                    { key: 'ozellik', text: service.ozellik },
                    { key: 'ozellik_1', text: service.ozellik_1 },
                    { key: 'ozellik_2', text: service.ozellik_2 },
                    { key: 'ozellik_3', text: service.ozellik_3 },
                  ].map((feature) => (
                    <motion.li
                      key={feature.key}
                      variants={itemVariants}
                      className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors duration-300"
                    >
                      <div className="mr-3 text-blue-600 group-hover:text-purple-600 transition-colors duration-300">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      {feature.text}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
